import React, { useMemo } from 'react';

import { InfoCircleFill as InfoCircleIcon } from '@styled-icons/bootstrap/InfoCircleFill';
import { Warning as WarningIcon } from '@styled-icons/fluentui-system-filled/Warning';

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
  Button,
  Icon,
  Text,
} from '@chakra-ui/react';

import { AlertRootProps } from 'components/Alert/Root';

import OutdatedComponentsTable from './ComponentsTable';
import { FlattenedComponent } from './types';

interface BudgetComponentListAlertProps extends AlertRootProps {
  data: {
    components: FlattenedComponent[];
    bdi: number;
  };
}

const BudgetComponentListAlert: React.FC<BudgetComponentListAlertProps> = ({
  type,
  data,
  buttons,
  onConfirm,
  handleClose,
  ...restProps
}) => {
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const icon = useMemo((): JSX.Element => {
    if (type === 'danger') {
      return <Icon as={WarningIcon} w={6} h={6} color="red.500" />;
    }

    if (type === 'warning') {
      return <Icon as={WarningIcon} w={6} h={6} color="orange.500" />;
    }

    return <Icon as={InfoCircleIcon} w={6} h={6} color="blue.300" />;
  }, [type]);

  const colorScheme = useMemo(() => {
    if (type === 'danger') {
      return 'red';
    }

    if (type === 'warning') {
      return 'orange';
    }

    return 'blue';
  }, [type]);

  return (
    <AlertDialog
      isOpen
      isCentered
      motionPreset="scale"
      leastDestructiveRef={cancelRef}
      onClose={handleClose}
      size="6xl"
      scrollBehavior="inside"
      {...restProps}
    >
      <AlertDialogOverlay />

      <AlertDialogContent>
        <AlertDialogHeader display="flex" alignItems="center" gap={2}>
          {icon} Item aparece múltiplas vezes no orçamento
        </AlertDialogHeader>

        <AlertDialogCloseButton />

        <AlertDialogBody>
          <Text fontSize="lg">
            O item que deseja alterar aparece em mais de um local do orçamento.
            Caso deseje prosseguir, todos os itens abaixo passarão a ter um BDI
            diferenciado de{' '}
            <Text as="span" fontWeight="bold">
              {Intl.NumberFormat('pt-BR', {
                maximumFractionDigits: 2,
              }).format(data.bdi || 0)}
              %
            </Text>
            .
          </Text>

          <OutdatedComponentsTable data={data.components} />
        </AlertDialogBody>

        <AlertDialogFooter>
          <Button ref={cancelRef} onClick={handleClose}>
            {buttons?.[0] || 'Cancelar'}
          </Button>

          <Button
            ml={3}
            colorScheme={colorScheme}
            onClick={() => {
              if (onConfirm) onConfirm(data.components.map((c) => c.id));
              handleClose();
            }}
          >
            {buttons?.[1] || 'Confirmar'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default BudgetComponentListAlert;
